import {
  createDatabaseBlockDataSource,
  type DatabaseBlockDataSource,
  isDatabaseStorageModelReady,
  observeDatabaseStorageModelReady,
} from '@blocksuite/affine/blocks/database';
import type { DatabaseBlockModel } from '@blocksuite/affine-model';
import { effect } from '@preact/signals-core';
import {
  createServerDatabaseRootDocId,
  normalizeDatabaseShardManifest,
} from '@streamlain/database-identity';
import { normalizeType } from '@streamlain/field-types';
import type { ViewConfigRow } from '@streamlain/view-config';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { DocService, DocsService } from '../../../../doc';

export interface ContentDbColumn {
  id: string;
  name: string;
  type: string;
}

type DatabaseRef = {
  blockId: string;
  databaseId?: string | null;
  docId?: string | null;
};

type ResolvedDatabaseRefModel = {
  model: DatabaseBlockModel;
  release?: () => void;
};

type ObservableSpaceDoc = {
  on: (event: 'update', callback: () => void) => void;
  off: (event: 'update', callback: () => void) => void;
};

export function resolveDatabaseRefModel(
  ref: DatabaseRef | null | undefined,
  docService: DocService,
  docsService?: DocsService
): DatabaseBlockModel | null {
  const resolved = resolveDatabaseRefModelHandle(ref, docService, docsService);
  try {
    return resolved?.model ?? null;
  } finally {
    resolved?.release?.();
  }
}

function resolveDatabaseRefModelHandle(
  ref: DatabaseRef | null | undefined,
  docService: DocService,
  docsService?: DocsService
): ResolvedDatabaseRefModel | null {
  if (!ref) return null;
  const anyDoc = docService.doc as any;
  const currentDocId: string | undefined = anyDoc?.id;
  const remoteDocId = ref.docId;
  const sameDoc = !remoteDocId || !currentDocId || remoteDocId === currentDocId;
  if (sameDoc) {
    const model = resolveDatabaseModelFromDoc(anyDoc?.blockSuiteDoc, ref);
    return model
      ? { model }
      : resolveCanonicalDatabaseRootModel(anyDoc?.blockSuiteDoc, ref);
  }
  if (!docsService) return null;

  try {
    const opened = docsService.open(remoteDocId) as {
      doc?: { blockSuiteDoc?: unknown };
      release?: () => void;
    };
    const model = resolveDatabaseModelFromDoc(opened.doc?.blockSuiteDoc, ref);
    if (!model) {
      opened.release?.();
      return resolveCanonicalDatabaseRootModel(anyDoc?.blockSuiteDoc, ref);
    }
    return { model, release: opened.release };
  } catch {
    return null;
  }
}

export function isDatabaseRefStorageReady(
  ref: DatabaseRef | null | undefined,
  docService: DocService,
  docsService?: DocsService
): boolean {
  const resolved = resolveDatabaseRefModelHandle(ref, docService, docsService);
  try {
    return resolved ? isDatabaseStorageModelReady(resolved.model) : false;
  } finally {
    resolved?.release?.();
  }
}

export function useDatabaseStorageRefsRevision(
  refs: ReadonlyArray<DatabaseRef | null | undefined>,
  docService: DocService,
  docsService?: DocsService
): number {
  const [revision, setRevision] = useState(0);
  const [modelDiscoveryRevision, setModelDiscoveryRevision] = useState(0);
  const refsRef = useRef(refs);
  refsRef.current = refs;
  const refsSignature = getDatabaseRefsSignature(refs);
  const notifiedReadyModelsRef = useRef<WeakSet<DatabaseBlockModel>>(
    new WeakSet()
  );

  useEffect(() => {
    const disposers: Array<() => void> = [];
    const releases: Array<() => void> = [];
    let disposed = false;
    let readyDuringEffect = false;
    const bump = () => {
      if (!disposed) setRevision(value => value + 1);
    };

    const models = refsRef.current
      .map(ref => resolveDatabaseRefModelHandle(ref, docService, docsService))
      .filter((resolved): resolved is ResolvedDatabaseRefModel =>
        Boolean(resolved)
      );
    const expectedModelCount = refsRef.current.filter(Boolean).length;

    if (models.length < expectedModelCount) {
      disposers.push(
        observeDatabaseRefModelDiscovery(refsRef.current, docService, () => {
          if (disposed) return;
          setModelDiscoveryRevision(value => value + 1);
          bump();
        })
      );

      // A detached root can finish hydrating after the initial lookup but
      // before its observers are installed. Recheck once after subscribing so
      // that this edge does not depend on another block update ever arriving.
      const modelsAfterSubscription = refsRef.current
        .map(ref => resolveDatabaseRefModelHandle(ref, docService, docsService))
        .filter((resolved): resolved is ResolvedDatabaseRefModel =>
          Boolean(resolved)
        );
      modelsAfterSubscription.forEach(model => model.release?.());
      if (modelsAfterSubscription.length > models.length) {
        setModelDiscoveryRevision(value => value + 1);
        bump();
      }
    }

    for (const model of models) {
      if (model.release) releases.push(model.release);
      if (isDatabaseStorageModelReady(model.model)) {
        if (!notifiedReadyModelsRef.current.has(model.model)) {
          notifiedReadyModelsRef.current.add(model.model);
          readyDuringEffect = true;
        }
        disposers.push(
          observeDatabaseSnapshotRevision(model.model, bump, docService)
        );
        continue;
      }
      disposers.push(
        observeDatabaseStorageModelReady(model.model, () => {
          if (notifiedReadyModelsRef.current.has(model.model)) return;
          notifiedReadyModelsRef.current.add(model.model);
          if (disposed) return;
          disposers.push(
            observeDatabaseSnapshotRevision(model.model, bump, docService)
          );
          bump();
        })
      );
    }
    if (readyDuringEffect) bump();

    return () => {
      disposed = true;
      disposers.forEach(dispose => dispose());
      releases.forEach(release => release());
    };
  }, [refsSignature, docService, docsService, modelDiscoveryRevision]);

  return revision;
}

function observeDatabaseRefModelDiscovery(
  refs: ReadonlyArray<DatabaseRef | null | undefined>,
  docService: DocService,
  onChange: () => void
): () => void {
  const disposers: Array<() => void> = [];
  const blockSuiteDoc = docService.doc.blockSuiteDoc as unknown as {
    slots?: {
      blockUpdated?: {
        subscribe?: (callback: () => void) => { unsubscribe?: () => void };
      };
    };
    spaceDoc?: {
      getMap?: (name: string) => {
        observe?: (callback: () => void) => void;
        unobserve?: (callback: () => void) => void;
      };
    };
    yDoc?: {
      getMap?: (name: string) => {
        observe?: (callback: () => void) => void;
        unobserve?: (callback: () => void) => void;
      };
    };
  };

  const subscription = blockSuiteDoc.slots?.blockUpdated?.subscribe?.(onChange);
  if (subscription?.unsubscribe) {
    disposers.push(() => subscription.unsubscribe?.());
  }

  const yDoc = blockSuiteDoc.spaceDoc ?? blockSuiteDoc.yDoc;
  const blocks = yDoc?.getMap?.('blocks');
  if (blocks?.observe && blocks.unobserve) {
    blocks.observe(onChange);
    disposers.push(() => blocks.unobserve?.(onChange));
  }

  for (const ref of refs) {
    const rootStore = resolveCanonicalDatabaseRootStore(blockSuiteDoc, ref);
    const rootSubscription =
      rootStore?.slots?.blockUpdated?.subscribe?.(onChange);
    if (rootSubscription?.unsubscribe) {
      disposers.push(() => rootSubscription.unsubscribe?.());
    }
    const rootYDoc = rootStore?.spaceDoc ?? rootStore?.yDoc;
    const rootBlocks = rootYDoc?.getMap?.('blocks');
    if (rootBlocks?.observe && rootBlocks.unobserve) {
      rootBlocks.observe(onChange);
      disposers.push(() => rootBlocks.unobserve?.(onChange));
    }
  }

  return () => {
    disposers.forEach(dispose => dispose());
  };
}

type CanonicalDatabaseRootRecord = {
  getStore?: (options: { id: string }) => CanonicalDatabaseRootStore | null;
  load?: () => unknown;
  ready?: boolean;
};

type CanonicalDatabaseRootStore = {
  getBlock?: (blockId: string) => { model?: unknown } | null;
  slots?: {
    blockUpdated?: {
      subscribe?: (callback: () => void) => { unsubscribe?: () => void };
    };
  };
  spaceDoc?: {
    getMap?: (name: string) => {
      observe?: (callback: () => void) => void;
      unobserve?: (callback: () => void) => void;
    };
  };
  yDoc?: CanonicalDatabaseRootStore['spaceDoc'];
};

function resolveCanonicalDatabaseRootRecord(
  blockSuiteDoc: unknown,
  ref: DatabaseRef | null | undefined
): CanonicalDatabaseRootRecord | null {
  if (!ref?.databaseId) return null;
  const workspace = (
    blockSuiteDoc as {
      workspace?: {
        id?: string;
        createDoc?: (id: string) => CanonicalDatabaseRootRecord;
        getDoc?: (id: string) => CanonicalDatabaseRootRecord | null;
        registerDoc?: (id: string) => unknown;
      };
    }
  )?.workspace;
  if (!workspace?.id) return null;
  const rootDocId = createServerDatabaseRootDocId({
    workspaceId: workspace.id,
    databaseId: ref.databaseId,
  });
  let root = workspace.getDoc?.(rootDocId);
  if (!root) {
    workspace.registerDoc?.(rootDocId);
    root = workspace.getDoc?.(rootDocId);
  }
  if (!root) root = workspace.createDoc?.(rootDocId);
  if (root && !root.ready) root.load?.();
  return root ?? null;
}

function resolveCanonicalDatabaseRootStore(
  blockSuiteDoc: unknown,
  ref: DatabaseRef | null | undefined
): CanonicalDatabaseRootStore | null {
  const root = resolveCanonicalDatabaseRootRecord(blockSuiteDoc, ref);
  if (!root || !ref?.databaseId) return null;
  const workspaceId = (
    blockSuiteDoc as { workspace?: { id?: string } } | null | undefined
  )?.workspace?.id;
  if (!workspaceId) return null;
  return (
    root.getStore?.({
      id: createServerDatabaseRootDocId({
        workspaceId,
        databaseId: ref.databaseId,
      }),
    }) ?? null
  );
}

function resolveCanonicalDatabaseRootModel(
  blockSuiteDoc: unknown,
  ref: DatabaseRef
): ResolvedDatabaseRefModel | null {
  const rootStore = resolveCanonicalDatabaseRootStore(blockSuiteDoc, ref);
  if (!rootStore || !ref.databaseId) return null;
  const model = resolveDatabaseModelFromDoc(rootStore, {
    ...ref,
    blockId: ref.databaseId,
  });
  return model ? { model } : null;
}

function getDatabaseRefsSignature(
  refs: ReadonlyArray<DatabaseRef | null | undefined>
): string {
  return refs
    .map(ref =>
      ref
        ? [ref.docId ?? '', ref.blockId, ref.databaseId ?? ''].join('\u0001')
        : ''
    )
    .join('\u0002');
}

function observeDatabaseSnapshotRevision(
  model: DatabaseBlockModel,
  onChange: () => void,
  docService?: DocService
): () => void {
  const dataSource = createDatabaseBlockDataSource(model);
  const disposeShardUpdates = observeDatabaseRowShardUpdates(
    dataSource.storageModel,
    onChange,
    docService
  );
  let didReadInitialSnapshot = false;
  const disposeSignalEffect = effect(() => {
    try {
      readDatabaseSnapshotFingerprint(dataSource);
    } catch {
      return;
    }
    if (didReadInitialSnapshot) {
      onChange();
      return;
    }
    didReadInitialSnapshot = true;
  });
  const disposePropsUpdated = dataSource.storageModel.propsUpdated.subscribe(
    ({ key }) => {
      if (
        key === 'columns' ||
        key === 'cells' ||
        key === 'databaseShardManifest'
      ) {
        if (key === 'databaseShardManifest') {
          disposeShardUpdates.reconcile();
        }
        onChange();
      }
    }
  );
  return () => {
    disposeSignalEffect();
    disposePropsUpdated.unsubscribe();
    disposeShardUpdates.dispose();
  };
}

function observeDatabaseRowShardUpdates(
  model: DatabaseBlockModel,
  onChange: () => void,
  docService?: DocService
): { dispose: () => void; reconcile: () => boolean } {
  const observed = new Map<
    string,
    { dispose: () => void; doc: ObservableSpaceDoc }
  >();
  const workspace = model.store?.workspace as
    | {
        createDoc?: (id: string) => {
          load?: () => unknown;
          ready?: boolean;
          spaceDoc?: ObservableSpaceDoc;
        };
        getDoc?: (id: string) => {
          load?: () => unknown;
          ready?: boolean;
          spaceDoc?: ObservableSpaceDoc;
        } | null;
        registerDoc?: (id: string) => unknown;
        slots?: {
          docListUpdated?: {
            subscribe: (callback: () => void) => {
              unsubscribe: () => void;
            };
          };
        };
      }
    | undefined;
  const requested = new Map<string, (() => void) | null>();
  const docEngine = (
    docService?.doc as unknown as {
      workspace?: {
        engine?: {
          doc?: {
            addDoc?: (docId: string, source: 'database-subdoc') => void;
            addPriority?: (docId: string, priority: number) => () => void;
          };
        };
      };
    }
  )?.workspace?.engine?.doc;

  const requestShard = (docId: string) => {
    if (!workspace || requested.has(docId)) return;
    docEngine?.addDoc?.(docId, 'database-subdoc');
    requested.set(docId, docEngine?.addPriority?.(docId, 90) ?? null);
    let shardDoc = workspace.getDoc?.(docId);
    if (!shardDoc) {
      workspace.registerDoc?.(docId);
      shardDoc = workspace.getDoc?.(docId);
    }
    if (!shardDoc) shardDoc = workspace.createDoc?.(docId);
    if (shardDoc && !shardDoc.ready) shardDoc.load?.();
  };

  const reconcile = () => {
    if (!workspace) return false;
    let manifest: ReturnType<typeof normalizeDatabaseShardManifest>;
    try {
      manifest = normalizeDatabaseShardManifest(
        model.props.databaseShardManifest
      );
    } catch {
      return false;
    }
    let changed = false;
    const expectedIds = new Set(
      (manifest?.shards ?? []).map(shard => shard.serverDocId)
    );

    for (const [docId, subscription] of observed) {
      const currentDoc = workspace.getDoc?.(docId)?.spaceDoc;
      if (expectedIds.has(docId) && currentDoc === subscription.doc) continue;
      subscription.dispose();
      observed.delete(docId);
      changed = true;
    }

    for (const docId of requested.keys()) {
      if (expectedIds.has(docId)) continue;
      requested.get(docId)?.();
      requested.delete(docId);
    }

    for (const docId of expectedIds) {
      requestShard(docId);
      const shardDoc = workspace.getDoc?.(docId);
      if (!shardDoc?.spaceDoc) continue;
      const spaceDoc = shardDoc.spaceDoc;
      const existing = observed.get(docId);
      if (existing?.doc === spaceDoc) continue;
      existing?.dispose();
      const callback = () => onChange();
      spaceDoc.on('update', callback);
      observed.set(docId, {
        doc: spaceDoc,
        dispose: () => spaceDoc.off('update', callback),
      });
      changed = true;
    }
    return changed;
  };

  reconcile();
  const disposeDocListUpdated = workspace?.slots?.docListUpdated?.subscribe(
    () => {
      if (reconcile()) onChange();
    }
  );
  return {
    reconcile,
    dispose: () => {
      disposeDocListUpdated?.unsubscribe();
      for (const subscription of observed.values()) subscription.dispose();
      observed.clear();
      for (const release of requested.values()) release?.();
      requested.clear();
    },
  };
}

function readDatabaseSnapshotFingerprint(
  dataSource: DatabaseBlockDataSource
): void {
  dataSource.getAllPropertyIds();
  dataSource.getLogicalRowIdsSnapshot();
}

export function getContentDbColumns(
  row: ViewConfigRow,
  docService: DocService,
  docsService?: DocsService
): ContentDbColumn[] {
  const ref = row.contentDbRef;
  if (!ref) return [];
  const resolved = resolveDatabaseRefModelHandle(ref, docService, docsService);
  try {
    if (!resolved || !isDatabaseStorageModelReady(resolved.model)) return [];
    const dataSource = createDatabaseBlockDataSource(resolved.model);
    const columns = ((dataSource.storageModel as any).props.columns ??
      []) as Array<{
      id: string;
      name: string;
      type: string;
    }>;
    return columns.map(c => ({
      id: c.id,
      name: c.name,
      type: normalizeType(c.type),
    }));
  } finally {
    resolved?.release?.();
  }
}

export function useContentDbColumns(
  row: ViewConfigRow | null | undefined,
  docService: DocService,
  docsService?: DocsService
): ContentDbColumn[] {
  const refs = useMemo(() => [row?.contentDbRef], [row?.contentDbRef]);
  const storageRevision = useDatabaseStorageRefsRevision(
    refs,
    docService,
    docsService
  );
  return useMemo(() => {
    void storageRevision;
    return row ? getContentDbColumns(row, docService, docsService) : [];
  }, [row, docService, docsService, storageRevision]);
}

function resolveDatabaseModelFromDoc(
  blockSuiteDoc: unknown,
  ref: DatabaseRef
): DatabaseBlockModel | null {
  const doc = blockSuiteDoc as
    | {
        getBlock?: (blockId: string) => { model?: unknown } | null;
        spaceDoc?: unknown;
        yDoc?: unknown;
      }
    | null
    | undefined;
  if (!doc?.getBlock) return null;
  const direct = doc.getBlock(ref.blockId);
  const directModel = direct?.model as DatabaseBlockModel | undefined;
  if (!ref.databaseId) return directModel ?? null;
  if (getModelDatabaseId(directModel) === ref.databaseId) {
    return directModel ?? null;
  }
  const blockId = findDatabaseBlockIdByDatabaseId(doc, ref.databaseId);
  if (!blockId) return null;
  const block = doc.getBlock(blockId);
  const model = block?.model as DatabaseBlockModel | undefined;
  return getModelDatabaseId(model) === ref.databaseId ? (model ?? null) : null;
}

function getModelDatabaseId(model: unknown): string | null {
  const databaseId = (model as { props?: Record<string, unknown> } | null)
    ?.props?.databaseId;
  return typeof databaseId === 'string' && databaseId.length > 0
    ? databaseId
    : null;
}

function findDatabaseBlockIdByDatabaseId(
  blockSuiteDoc: { spaceDoc?: unknown; yDoc?: unknown },
  databaseId: string
): string | null {
  const yDoc = (blockSuiteDoc.spaceDoc ?? blockSuiteDoc.yDoc) as
    | {
        getMap?: (name: string) => {
          forEach?: (
            callback: (
              value: { get?: (key: string) => unknown },
              key: string
            ) => void
          ) => void;
        };
      }
    | undefined;
  const blocks = yDoc?.getMap?.('blocks');
  let matchedBlockId: string | null = null;
  blocks?.forEach?.((block, blockId) => {
    if (matchedBlockId) return;
    const flavour = block.get?.('sys:flavour');
    if (typeof flavour === 'string' && flavour !== 'affine:database') {
      return;
    }
    if (block.get?.('prop:databaseId') === databaseId) {
      matchedBlockId = blockId;
    }
  });
  return matchedBlockId;
}
